if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.injectMediaNavigationStyles();
        this.addMediaNavigationButtons();
        this.setupDesktopDescriptionLayout();
        this.elements.viewer.addEventListener('click', this.onViewerClick.bind(this), true);
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const thumbnailButton = mediaToSwitch.querySelector('button');
          if (!thumbnailButton) return;
          const setActiveWithoutScroll = (event) => {
            if (event) event.preventDefault();
            this.setActiveMedia(mediaToSwitch.dataset.target, false, false);
          };
          const setActiveOnHover = (event) => {
            if (!this.mql.matches) return;
            if (event.pointerType === 'touch') return;
            setActiveWithoutScroll(event);
          };

          thumbnailButton.addEventListener('click', setActiveWithoutScroll);
          mediaToSwitch.addEventListener('mouseenter', setActiveOnHover);
          mediaToSwitch.addEventListener('pointerenter', setActiveOnHover);
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      onViewerClick(event) {
        const navButton = event.target.closest('[data-media-nav-direction]');
        if (!navButton) return;

        const modalOpener = navButton.closest('.product__modal-opener--image');
        if (!modalOpener) return;

        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

        this.showAdjacentMedia(Number(navButton.dataset.mediaNavDirection));
      }

      getActiveMedia() {
        return (
          this.elements.viewer.querySelector('.product__media-item.is-active[data-media-id]') ||
          this.elements.viewer.querySelector('.product__media-item[data-media-id]')
        );
      }

      showAdjacentMedia(direction) {
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        if (mediaItems.length < 2) return;

        const activeMedia = this.getActiveMedia();
        const activeIndex = mediaItems.indexOf(activeMedia);
        if (activeIndex === -1) return;

        const nextIndex = (activeIndex + direction + mediaItems.length) % mediaItems.length;
        this.setActiveMedia(mediaItems[nextIndex].dataset.mediaId, false, false);
      }

      injectMediaNavigationStyles() {
        if (document.getElementById('product-media-navigation-styles')) return;
        const style = document.createElement('style');
        style.id = 'product-media-navigation-styles';
        style.textContent = `
          .product-media-nav-button {
            align-items: center;
            background: rgba(var(--color-background), 0.72);
            border: 0.1rem solid rgba(var(--color-foreground), 0.16);
            border-radius: 999px;
            color: rgb(var(--color-foreground));
            cursor: pointer;
            display: flex;
            font-size: 3.2rem;
            height: 4.4rem;
            justify-content: center;
            line-height: 1;
            opacity: 0;
            padding: 0 0 0.3rem;
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            transition: opacity var(--duration-short) ease, background var(--duration-short) ease;
            width: 4.4rem;
            z-index: 3;
          }

          .product-media-nav-button--previous {
            left: 1.2rem;
          }

          .product-media-nav-button--next {
            right: 1.2rem;
          }

          .product__modal-opener--image:hover .product-media-nav-button,
          .product-media-nav-button:focus-visible {
            opacity: 1;
          }

          .product-media-nav-button:hover {
            background: rgba(var(--color-background), 0.94);
          }

          .product__description--media-copy {
            display: none;
          }

          @media screen and (min-width: 990px) {
            .product__media-wrapper {
              overflow: visible;
            }

            .product__media-wrapper .thumbnail-slider {
              width: min(178%, calc(100vw - 6rem));
            }

            .product__media-wrapper .thumbnail-list.slider--tablet-up .thumbnail-list__item.slider__slide {
              width: calc(10% - 0.9rem);
            }

            .product__media-wrapper .thumbnail-list:not(.slider--tablet-up) {
              grid-template-columns: repeat(10, 1fr);
            }

            .product__info-wrapper .price {
              font-size: 1.24em;
            }

            .product__info-wrapper .product__tax {
              margin-bottom: 3.2rem;
            }

            .product__info-wrapper .product-form__quantity {
              margin-bottom: 3.2rem;
            }

            .product__media-wrapper .product__description--media-copy {
              color: rgba(var(--color-foreground), 0.82);
              display: block;
              font-size: 1.7rem;
              line-height: 1.9;
              margin: 4.4rem 0 0;
              max-width: none;
              width: min(178%, calc(100vw - 6rem));
            }

            .product__media-wrapper .product-description-meta-line {
              background: #f4efe8;
              margin-bottom: 0;
              padding: 0.2rem 2.8rem;
            }

            .product__media-wrapper .product-description-meta-line--first {
              border-radius: 0.4rem 0.4rem 0 0;
              padding-top: 2.4rem;
            }

            .product__media-wrapper .product-description-meta-line--last {
              border-radius: 0 0 0.4rem 0.4rem;
              margin-bottom: 2.4rem;
              padding-bottom: 2.4rem;
            }

            .product__info-wrapper .product__description:not(.product__description--media-copy) {
              display: none;
            }
          }
        `;
        document.head.appendChild(style);
      }

      setupDesktopDescriptionLayout() {
        const product = this.closest('.product');
        const mediaWrapper = this.closest('.product__media-wrapper');
        if (!product || !mediaWrapper || mediaWrapper.querySelector('.product__description--media-copy')) return;

        const description = product.querySelector('.product__info-wrapper .product__description');
        if (!description || !description.textContent.trim()) return;

        const mediaDescription = description.cloneNode(true);
        mediaDescription.removeAttribute('id');
        mediaDescription.classList.add('product__description--media-copy');
        mediaDescription.setAttribute('aria-hidden', 'true');
        this.formatProductMetadata(mediaDescription);
        mediaWrapper.appendChild(mediaDescription);
      }

      formatProductMetadata(description) {
        const elements = Array.from(description.children);
        const startIndex = elements.findIndex((element) => (element.textContent || '').includes('[タイトル]'));
        if (startIndex === -1) return;

        const endIndex = elements.findIndex((element, index) => index > startIndex && element.matches('h3'));
        if (endIndex === -1) return;

        const metadataElements = elements.slice(startIndex, endIndex);
        const bracketCount = metadataElements.reduce((count, element) => {
          const matches = (element.textContent || '').match(/\[[^\]]+\]/g);
          return count + (matches ? matches.length : 0);
        }, 0);

        if (metadataElements.length === 0 || bracketCount < 3) return;

        metadataElements.forEach((element, index) => {
          element.classList.add('product-description-meta-line');
          if (index === 0) element.classList.add('product-description-meta-line--first');
          if (index === metadataElements.length - 1) element.classList.add('product-description-meta-line--last');
        });
      }

      addMediaNavigationButtons() {
        this.elements.viewer.querySelectorAll('.product__modal-opener--image').forEach((modalOpener) => {
          if (modalOpener.querySelector('[data-media-nav-direction]')) return;
          const previousButton = document.createElement('button');
          previousButton.type = 'button';
          previousButton.className = 'product-media-nav-button product-media-nav-button--previous';
          previousButton.dataset.mediaNavDirection = '-1';
          previousButton.setAttribute('aria-label', 'Previous image');
          previousButton.innerHTML = '<span aria-hidden="true">&#8249;</span>';

          const nextButton = document.createElement('button');
          nextButton.type = 'button';
          nextButton.className = 'product-media-nav-button product-media-nav-button--next';
          nextButton.dataset.mediaNavDirection = '1';
          nextButton.setAttribute('aria-label', 'Next image');
          nextButton.innerHTML = '<span aria-hidden="true">&#8250;</span>';

          modalOpener.append(previousButton, nextButton);
        });
      }

      setActiveMedia(mediaId, prepend, shouldScroll = true) {
        const activeMedia =
          this.elements.viewer.querySelector(`.product__media-item[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.product__media-item[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        if (shouldScroll) {
          this.preventStickyHeader();
          window.setTimeout(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
            const activeMediaRect = activeMedia.getBoundingClientRect();
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          });
        }
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail, shouldScroll);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail, shouldScroll = true) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (!shouldScroll || this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
